<template>
  <div class="delivery-page">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">Delivery Trips</h1>
      <button class="btn-primary" @click="openCreate">+ New Trip</button>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <!-- Date navigator -->
      <div class="date-nav">
        <button class="date-arrow" @click="prevDay" title="Previous day">
          ‹
        </button>
        <input
          class="filter-input date-input"
          type="date"
          v-model="filters.date"
          @change="load"
        />
        <button
          class="date-arrow"
          @click="nextDay"
          :disabled="isToday"
          title="Next day"
        >
          ›
        </button>
        <button
          v-if="!isToday"
          class="btn-secondary today-btn"
          @click="goToday"
        >
          Today
        </button>
        <span v-if="relativeLabel" class="relative-label">
          {{ relativeLabel }}
        </span>
      </div>

      <select class="filter-select" v-model="filters.department" @change="load">
        <option value="">All departments</option>
        <option
          v-for="d in departments"
          :key="d.departamentoId"
          :value="d.codigo"
        >
          {{ d.codigo }} — {{ d.nombre }}
        </option>
      </select>
      <select class="filter-select" v-model="filters.status" @change="load">
        <option value="">All statuses</option>
        <option value="draft">Draft</option>
        <option value="in_progress">In Progress</option>
        <option value="completed">Completed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <span class="count">{{ trips.length }} trips</span>
    </div>

    <!-- Table -->
    <div class="table-wrap">
      <div v-if="loading" class="empty-state">Loading...</div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Driver</th>
            <th>Orders</th>
            <th>Status</th>
            <th>Started</th>
            <th>Completed</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="trip in trips" :key="trip.uuid">
            <td>
              <div class="date-cell">
                <span>{{ formatDate(trip.createdAt) }}</span>
                <span
                  v-if="_relativeDateLabel(trip.createdAt.slice(0, 10))"
                  class="relative-label relative-label--sm"
                >
                  {{ _relativeDateLabel(trip.createdAt.slice(0, 10)) }}
                </span>
              </div>
            </td>
            <td>
              <div class="driver-cell">
                <div
                  class="avatar"
                  :style="{ background: avatarColor(trip.driverName) }"
                >
                  {{ initials(trip.driverName) }}
                </div>
                <span>{{ trip.driverName || "—" }}</span>
              </div>
            </td>
            <td>
              <div
                class="orders-summary"
                @click="goToDetail(trip.uuid)"
                title="View orders"
              >
                <span class="count-badge">{{ trip.ordersCount || 0 }}</span>
                <span
                  v-if="trip.ordersDelivered > 0"
                  class="mini-badge mini-badge--delivered"
                >
                  ✓ {{ trip.ordersDelivered }}
                </span>
                <span
                  v-if="trip.ordersPending > 0"
                  class="mini-badge mini-badge--pending"
                >
                  ● {{ trip.ordersPending }}
                </span>
                <span
                  v-if="trip.ordersFailed > 0"
                  class="mini-badge mini-badge--failed"
                >
                  ✕ {{ trip.ordersFailed }}
                </span>
              </div>
            </td>
            <td>
              <span class="status-badge" :class="statusClass(trip.status)">
                {{ statusLabel(trip.status) }}
              </span>
            </td>
            <td>{{ trip.startedAt ? formatDate(trip.startedAt) : "—" }}</td>
            <td>
              {{ trip.completedAt ? formatDate(trip.completedAt) : "—" }}
            </td>
            <td>
              <button class="link-blue" @click="goToDetail(trip.uuid)">
                View
              </button>
              <button
                v-if="trip.status === 'draft' || trip.status === 'in_progress'"
                class="link-red ml-2"
                @click="cancelTrip(trip)"
              >
                Cancel
              </button>
            </td>
          </tr>
          <tr v-if="!trips.length && !loading">
            <td colspan="7" class="empty-state">No trips found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- New trip modal -->
    <BaseModal v-model="showModal" @close="showModal = false">
      <div class="p-6" style="min-width: 380px">
        <h3 class="text-lg font-semibold mb-4">New Delivery Trip</h3>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Department *</label>
          <select
            v-model="newTrip.departamentoId"
            class="w-full border rounded px-3 py-2"
            @change="newTrip.originDepositoId = ''"
          >
            <option value="">Select department...</option>
            <option
              v-for="d in departments"
              :key="d.departamentoId"
              :value="d.codigo"
            >
              {{ d.codigo }} — {{ d.nombre }}
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Warehouse *</label>
          <select
            v-model="newTrip.originDepositoId"
            class="w-full border rounded px-3 py-2"
            :disabled="!newTrip.departamentoId"
          >
            <option value="">
              {{
                newTrip.departamentoId
                  ? "Select warehouse..."
                  : "Select a department first"
              }}
            </option>
            <option
              v-for="dep in filteredDepositos"
              :key="dep.depositoId"
              :value="dep.depositoId"
            >
              {{ dep.descDeposito || dep.codDeposito }}
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Driver *</label>
          <select
            v-model="newTrip.driverUserUuid"
            class="w-full border rounded px-3 py-2"
          >
            <option value="">Select driver...</option>
            <optgroup v-if="driverUsers.length" label="Drivers">
              <option v-for="u in driverUsers" :key="u.uuid" :value="u.uuid">
                {{ u.name || u.username || u.email }} — {{ u.role }}
              </option>
            </optgroup>
            <optgroup v-if="otherUsers.length" label="Other users">
              <option v-for="u in otherUsers" :key="u.uuid" :value="u.uuid">
                {{ u.name || u.username || u.email }} — {{ u.role }}
              </option>
            </optgroup>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Notes (optional)</label>
          <textarea
            v-model="newTrip.notes"
            class="w-full border rounded px-3 py-2"
            rows="3"
            placeholder="Internal notes..."
          />
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100"
            @click="showModal = false"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            @click="saveNewTrip"
            :disabled="
              saving ||
                !newTrip.departamentoId ||
                !newTrip.originDepositoId ||
                !newTrip.driverUserUuid
            "
          >
            {{ saving ? "Creating..." : "Create" }}
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { getTrips, createTrip, cancelTrip } from "@/api/DeliveryService.js";
import { getDepositos } from "@/api/Infoauto.js";
import { getUsers } from "@/api/UsersService.js";

const AVATAR_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f59e0b",
  "#3b82f6",
  "#10b981",
  "#f97316"
];

export default {
  name: "DeliveryTripsPage",
  data() {
    return {
      trips: [],
      loading: false,
      saving: false,
      showModal: false,
      filters: {
        date: this.todayStr(),
        department: this.$route.params.departmentCode || "",
        status: ""
      },
      newTrip: {
        notes: "",
        departamentoId: "",
        originDepositoId: "",
        driverUserUuid: ""
      },
      depositos: [],
      users: []
    };
  },
  computed: {
    departments() {
      return this.$store.getters["general/getDepartments"] || [];
    },
    filteredDepositos() {
      if (!this.newTrip.departamentoId) return [];
      const dept = this.departments.find(
        d => d.codigo === this.newTrip.departamentoId
      );
      if (!dept) return [];
      return this.depositos.filter(
        d => String(d.departamentoId) === String(dept.departamentoId)
      );
    },
    enabledUsers() {
      return (this.users || []).filter(u => u.enabled);
    },
    driverUsers() {
      return this.enabledUsers
        .filter(u => u.role === "driver")
        .sort((a, b) =>
          (a.name || a.email || "").localeCompare(b.name || b.email || "")
        );
    },
    otherUsers() {
      return this.enabledUsers
        .filter(u => u.role !== "driver")
        .sort((a, b) =>
          (a.name || a.email || "").localeCompare(b.name || b.email || "")
        );
    },
    isToday() {
      return this.filters.date === this.todayStr();
    },
    relativeLabel() {
      return this._relativeDateLabel(this.filters.date);
    }
  },
  async mounted() {
    this.load();
    try {
      this.depositos = await getDepositos();
    } catch (e) {
      this.depositos = [];
    }
    try {
      const res = await getUsers({ limit: 1000 });
      this.users = res.items || res || [];
    } catch (e) {
      this.users = [];
    }
  },
  methods: {
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    _relativeDateLabel(dateStr) {
      if (!dateStr) return "";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(dateStr + "T12:00:00");
      target.setHours(0, 0, 0, 0);
      const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "Today";
      if (diffDays === -1) return "Yesterday";
      if (diffDays === 1) return "Tomorrow";
      return "";
    },
    prevDay() {
      const d = new Date(this.filters.date + "T12:00:00");
      d.setDate(d.getDate() - 1);
      this.filters.date = d.toISOString().slice(0, 10);
      this.load();
    },
    nextDay() {
      if (this.isToday) return;
      const d = new Date(this.filters.date + "T12:00:00");
      d.setDate(d.getDate() + 1);
      this.filters.date = d.toISOString().slice(0, 10);
      this.load();
    },
    goToday() {
      this.filters.date = this.todayStr();
      this.load();
    },
    async load() {
      this.loading = true;
      try {
        const params = {
          date_from: this.filters.date + " 00:00:00",
          date_to: this.filters.date + " 23:59:59"
        };
        if (this.filters.department)
          params.department = this.filters.department;
        if (this.filters.status) params.status = this.filters.status;
        this.trips = await getTrips(params);
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      const onlyDriver =
        this.driverUsers.length === 1 ? this.driverUsers[0].uuid : "";
      this.newTrip = {
        notes: "",
        departamentoId: this.filters.department || "",
        originDepositoId: "",
        driverUserUuid: onlyDriver
      };
      this.showModal = true;
    },
    async saveNewTrip() {
      if (
        !this.newTrip.departamentoId ||
        !this.newTrip.originDepositoId ||
        !this.newTrip.driverUserUuid
      )
        return;
      this.saving = true;
      try {
        const res = await createTrip({
          notes: this.newTrip.notes || null,
          departamento_id: this.newTrip.departamentoId,
          origin_deposito_id: this.newTrip.originDepositoId,
          driver_user_uuid: this.newTrip.driverUserUuid
        });
        this.showModal = false;
        this.$router.push({
          name: "DeliveryTripDetailPage",
          params: { uuid: res.uuid },
          query: { department: this.newTrip.departamentoId }
        });
      } catch (e) {
        alert(e?.response?.data?.message || "Error creating trip");
      } finally {
        this.saving = false;
      }
    },
    async cancelTrip(trip) {
      if (!confirm(`Cancel trip from ${trip.driverName || "driver"}?`)) return;
      try {
        await cancelTrip(trip.uuid);
        this.load();
      } catch (e) {
        alert("Error cancelling trip");
      }
    },
    goToDetail(uuid) {
      this.$router.push({
        name: "DeliveryTripDetailPage",
        params: { uuid },
        query: this.filters.department
          ? { department: this.filters.department }
          : {}
      });
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    statusLabel(s) {
      return (
        {
          draft: "Draft",
          in_progress: "In Progress",
          completed: "Completed",
          cancelled: "Cancelled"
        }[s] || s
      );
    },
    statusClass(s) {
      return {
        "status--draft": s === "draft",
        "status--progress": s === "in_progress",
        "status--completed": s === "completed",
        "status--cancelled": s === "cancelled"
      };
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map(w => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarColor(name) {
      if (!name) return AVATAR_COLORS[0];
      return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
    }
  }
};
</script>

<style lang="scss" scoped>
.delivery-page {
  width: 100%;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.page-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.btn-primary {
  background: #2563eb;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  &:hover {
    background: #1d4ed8;
  }
}
.btn-secondary {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: #fff;
  cursor: pointer;
  &:hover {
    background: #f3f4f6;
  }
}
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
}
.filter-select,
.filter-input {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}
.date-nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.date-arrow {
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}
.date-input {
  width: 140px;
}
.today-btn {
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
}
.relative-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  &--sm {
    font-size: 0.65rem;
    padding: 0.1rem 0.45rem;
  }
}
.date-cell {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.count {
  font-size: 0.8rem;
  color: #9ca3af;
  margin-left: auto;
}
.table-wrap {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: auto;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th,
  td {
    text-align: left;
    padding: 0.625rem 0.875rem;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
  }
  tbody tr:hover {
    background: #f9fafb;
  }
}
.driver-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.avatar {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.count-badge {
  display: inline-block;
  background: #e0e7ff;
  color: #4338ca;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  cursor: pointer;
  &:hover {
    background: #c7d2fe;
  }
}
.status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  &.status--draft {
    background: #f3f4f6;
    color: #6b7280;
  }
  &.status--progress {
    background: #fef3c7;
    color: #b45309;
  }
  &.status--completed {
    background: #dcfce7;
    color: #15803d;
  }
  &.status--cancelled {
    background: #fee2e2;
    color: #b91c1c;
  }
}
.link-blue {
  color: #2563eb;
  font-size: 0.8rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.link-red {
  color: #dc2626;
  font-size: 0.8rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.empty-state {
  padding: 2.5rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}
</style>
